import { useQuery } from "@apollo/client/react";
import { useState } from "react";

import { useSearchParams } from "react-router-dom";

import { Typography } from "@mui/material";
import { FunnelIcon } from "@phosphor-icons/react/dist/csr/Funnel";
import { ExportStatus, ExportStatusDocument } from "../../graphql/types";
import type { UploadJournal } from "../../graphql/schema-types";
import type { ExportStatusQuery, ExportStatusQueryVariables } from "../../graphql/types";
import { QueryResult } from "../common/QueryResult";
import { LinkUploadsByProject } from "../LinksHelper";
import { StatusChart } from "../StatusChart";
import { buildStats } from "./buildStats";
import { DefaultPanel } from "../ui/panels";
import { createDefaultExportStatusFilters, ExportStatusFilters } from "../UploadFilters";
import type { IExportStatusFilter } from "../UploadFilters";
import { BaseWelcomeMessage } from "../WelcomeMessage";

const PAGE_SIZE = 1000;
const DEFAULT_LOOKBACK_DAYS = 30;

const ExportStatusList = ({ rows, project = "" }: { rows: ExportStatus[]; project?: string | null }) => {
  const filteredExports = rows.filter((exportStatus: ExportStatus) => {
    return project === null || project === "" || exportStatus.project === project;
  });

  const exportStatus = filteredExports.map((exportStatus: ExportStatus, index: number) => {
    const journalByDate = exportStatus.journal?.reduce<Record<string, UploadJournal>>(function (map, obj) {
      if (obj) {
        map[String(obj.date)] = obj;
      }
      return map;
    }, {});
    const exportStatusWithStats = {
      ...exportStatus,
      journal: buildStats(DEFAULT_LOOKBACK_DAYS, journalByDate),
    };
    const viewFiles = exportStatusWithStats.project ? (
      <LinkUploadsByProject file_path={exportStatusWithStats.project} />
    ) : null;
    return (
      <StatusChart
        key={index}
        data={exportStatusWithStats}
        prefix={"Uploads"}
        viewFiles={viewFiles}
        assetType={"Export"}
      />
    );
  });

  return <div>{exportStatus}</div>;
};

const NoFilters = () => (
  <BaseWelcomeMessage icon={FunnelIcon} title={"Select a Project or Export"}>
    <Typography>Select a Project or Export to view upload activity.</Typography>
  </BaseWelcomeMessage>
);

const ExportStatusDashboardCharts = ({
  dateRangeType,
  exportName: exportNameProp,
  project: projectProp,
  first = PAGE_SIZE,
}: IExportStatusFilter & { first?: number }) => {
  const exportName = exportNameProp ?? "";
  const project = projectProp ?? "";
  const hasValue = exportName !== "" || project !== "";

  const variables = {
    first,
    lookback_days: DEFAULT_LOOKBACK_DAYS,
    name: exportName,
    project,
    group_by: dateRangeType,
  };
  const { loading, error, data } = useQuery<ExportStatusQuery, ExportStatusQueryVariables>(ExportStatusDocument, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables,
  });

  if (hasValue) {
    return (
      <QueryResult
        loading={loading}
        error={error}
        data={data}
        nodeName="export_status"
        emptyElement="No Exports"
        resultsMapper={(results: Array<ExportStatus>) => <ExportStatusList rows={results || []} project={project} />}
      />
    );
  } else {
    return <NoFilters />;
  }
};

const ExportStatusDashboard = () => {
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(
    createDefaultExportStatusFilters({
      project: searchParams.get("project_id"),
      exportName: searchParams.get("export_name"),
    })
  );
  return (
    <DefaultPanel.Container>
      <DefaultPanel.Header>
        <ExportStatusFilters
          {...filter}
          onChange={setFilter}
          onClear={() => setFilter(createDefaultExportStatusFilters({}))}
        />
      </DefaultPanel.Header>
      <DefaultPanel.Body>
        <div className="ExportStatusDashboard" data-cy="export-status-dashboard">
          <ExportStatusDashboardCharts {...filter} />
        </div>
      </DefaultPanel.Body>
    </DefaultPanel.Container>
  );
};

export { ExportStatusDashboard };
